import CloseIcon from '@mui/icons-material/Close';
import { Button,Dialog,DialogActions,DialogContent,DialogTitle,IconButton,Skeleton,Stack,Typography } from '@mui/material';
import { useMutation,useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import AttToken from '../../../../helpers/attToken';
import { trackEventMatomo } from '../../../../helpers/matomo';
import Places,{ PlaceType } from '../../../../service/Places';
import queryClient from '../../../../service/query';
import ServiceTypes from '../../../../service/ServiceTypes';
import GenericAutoComplete from '../../../GenericAutoComplete';

type props = {
  open: boolean,
  setOpen: Function,
  place: PlaceType
};

const PlaceService = new Places();
const services = new ServiceTypes();

export default function PlaceTypeAssoci({ open, setOpen, place }: props) {
  const { t } = useTranslation('translation');
  const [selectedServices, setSelectedServices] = useState<string[]>(place.tiposServico ?? []);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['GET_SERVICES_FOR_PLACES'],
    queryFn: async () => {
      const promise = toast.promise(async () => {
        const token = await AttToken();
        if (token) {
          const response = await services.getAllByCompany({ ...token, empresa: token.userEmpresa });
          if (!response) {
            throw new Error('Error fetching services');
          }
          return response;
        }
      }, {
        // success: t('Services.ServiceModal.noServiceTypesAvailable'),
        error: t('Reports.reportsTable.errorFetchingData')
      }, { toastId: 'buscandoServicos' });
      return promise;
    }
  });

  const { mutate, isPending } = useMutation({
    mutationKey: ['UPDATE_PLACE'],
    mutationFn: async () => {
      const promise = toast.promise(async () => {
        const token = await AttToken();
        if (token) {
          const res = await PlaceService.addServiceType({ ...token }, selectedServices, place?.id ?? '');
          if (!res) {
            throw new Error('Error updating place');
          }
          return res;
        }
      }, {
        success: t('Services.ServiceModal.typesAddedSuccessfully'),
        error: t('Services.ServiceModal.errorAddingTypes')
      }, { toastId: 'salvando' });
      return promise;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['GET_PLACES'] });
      queryClient.resetQueries({ queryKey: ['GET_CLIENTES'] });
      setOpen(false);
      trackEventMatomo('Modal associação de clientes', 'click', 'button', 'salva');
    },
  });

  const onLoading = isPending || isFetching || isLoading;

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      scroll={'paper'}
    >
      <DialogTitle>{t('places.Associar_Tipos_de_Serviço')}</DialogTitle>
      <IconButton
        onClick={() => setOpen(false)}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers={true}>
        <Stack direction='column' width='100%' alignItems='flex-start' justifyContent='flex-start' spacing={2}>
          {isLoading
            ? <Skeleton width='100%' variant='rounded' height={50} />
            : <GenericAutoComplete
              selectedValues={selectedServices ?? []}
              label={t('Services.servicesManagement.kind')}
              options={(data as { nome: string, tipoId: string }[])
                ?.sort((a, b) => { return a.nome.toLowerCase() > b.nome.toLowerCase() ? 1 : - 1; })
                ?.map((ele) => ({
                  label: ele.nome,
                  value: ele.tipoId,
                })) ?? []}
              onChange={(values) => setSelectedServices(values)}
            />
          }
          <Typography textAlign='center' fontWeight={600} fontSize='small' color='textDisabled'>
            {t('places.click_to_save')}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => mutate()} disabled={selectedServices.length === 0 || onLoading}>{t('save')}</Button>
      </DialogActions>
    </Dialog>
  );
}
